/**
 * Card Component Registry
 *
 * Central registry mapping resource types (e.g. 'listing', 'event') to their
 * dedicated card components. The blocks feed resolves each item's card via
 * `getCardComponent(type)` without inline if/else branching.
 *
 * To register a new card type, import its component and call:
 *   registerCard('myType', MyTypeCard);
 */

import { ListingCard } from './ListingCard.js';
import { EventCard } from './EventCard.js';
import { PageCard } from './PageCard.js';
import { OfferCard } from './OfferCard.js';
import { DefaultCard } from './DefaultCard.js';

const registry = new Map();

/**
 * Registers a card component for a given resource type.
 * Duplicate type keys overwrite the previous entry.
 *
 * @param {string} type - The resource type identifier (e.g. 'listing', 'event').
 * @param {Function} component - The Preact card component.
 */
export function registerCard(type, component) {
  registry.set(type, component);
}

/**
 * Resolves the card component for a given resource type.
 * Returns `DefaultCard` if no dedicated component is registered.
 *
 * @param {string} type - The resource type identifier.
 * @returns {Function} The Preact card component for the given type.
 */
export function getCardComponent(type) {
  return registry.get(type) || DefaultCard;
}

// Register built-in card types
registerCard('listing', ListingCard);
registerCard('event', EventCard);
registerCard('page', PageCard);
registerCard('article', PageCard);
registerCard('offer', OfferCard);
